import MetricsRepositoryInterface from "src/survey/metrics/MetricsRepostoryInterface";
import { DURATIONS } from "src/shared/VibesTargeter";
import PulseTnTService from "src/shared/pulseTnTService";
import { getCsrfToken } from "src/shared/getMetaData";

type DurationSeconds = number;

/**
 * This class is responsible for saving/fetching data from the parent data store
 */
export default class TrackingRepository {
    private readonly storage: Storage;
    private readonly metricsRepo?: MetricsRepositoryInterface;

    constructor(storage: Storage, metricsRepo?: MetricsRepositoryInterface) {
        this.storage = storage;

        if (metricsRepo) {
            this.metricsRepo = metricsRepo;
        }
    }

    /**
     * Track a user's interaction with the specified workflow for a number of seconds
     *
     * @param workflow
     * @param ttl
     */
    public trackWorkflow(workflow: string, ttl: number) {
        const time = Date.now() + ttl * 1000;
        this.storage.setItem(`vibes:${workflow}`, time.toString());

        if (this.metricsRepo) {
            this.metricsRepo.workflowTrackingEvent(workflow);
        }
    }

    /**
     * Returns a list of vibes workflows currently stored
     */
    public getWorkflowList(): string[] {
        const keys = Object.keys(this.storage);
        const foundKeys: string[] = [];

        for (const key of keys) {
            if (key.startsWith('vibes:')) {
                const workflow = key.substr(6);

                if (!this.isWorkflowRecent(workflow)) {
                    foundKeys.push(workflow);
                }
            }
        }

        return foundKeys;
    }

    /**
     * Check the local storage cache for a workflow. Deletes the key if the TTL is
     * expired.
     *
     * @param workflow
     */
    public hasWorkflow(workflow: string): boolean {
        const result = this.storage.getItem(`vibes:${workflow}`);

        if (!result) {
            return false;
        }

        const ttl = parseInt(result);

        // The time could not be parsed, or the date is expired
        if (!ttl || ttl < Date.now()) {
            this.storage.removeItem(`vibes:${workflow}`);
            return false;
        }

        return true;
    }

    /**
     * Check the local storage cache for a workflow. Deletes the key if the TTL is
     * expired.
     *
     * @param workflow
     */
    public getWorkflowTTL(workflow: string): false|number {
        const result = this.storage.getItem(`vibes:${workflow}`);

        if (!result) {
            return false;
        }

        const ttl = parseInt(result);

        // The time could not be parsed, or the date is expired
        if (!ttl || ttl < Date.now()) {
            this.storage.removeItem(`vibes:${workflow}`);
            return false;
        }

        return ttl;
    }

    public isWorkflowRecent(workflow: string): boolean {
        const ttl = this.getWorkflowTTL(workflow);

        if (!ttl) {
            return false;
        }

        const timeSet = ttl - DURATIONS.twoWeeks * 1000;

        // How much time has passed since setting the record
        const timePassed = Date.now() - timeSet;

        // If we tracked the page within the last hour, record is recent
        // do not update tracking
        return timePassed < (DURATIONS.oneHour * 1000);
    }

    public getRateOverride(): false|number {
        const result = this.storage.getItem(`vibes-rate-override`);

        if (!result) {
            return false;
        }

        return parseFloat(result);
    }

    /**
     * Save that a user has elected to be asked later
     *
     * @param workflow
     * @param waitTime
     * @param questionId Pulse Question ID
     */
    public saveAskMeLater(workflow: string, waitTime: number, questionId?: string) {
        if (workflow === 'pulse' && questionId) {
            const pulseClient = new PulseTnTService(process.env.API_HOST!, getCsrfToken(window.parent.document));
            pulseClient.registerEvent(questionId, 'DEFERRED', window.parent.pulseEventId, window.parent.document);
        }
        const data = {
          time: (Date.now() + waitTime * 1000).toString(),
          workflow,
        };

        this.storage.setItem(`vibes-aml`, JSON.stringify(data));
        this.metricsRepo && this.metricsRepo.askLaterEvent(workflow);
    }

    /**
     * Save that a user has dismissed the vibes survey
     */
    public saveDismiss(workflow: string, context: Document, questionId?: string) {
        if (workflow === 'pulse' && questionId) {
            const pulseClient = new PulseTnTService(process.env.API_HOST!, getCsrfToken(context));
            pulseClient.registerEvent(questionId, 'DISMISSED', window.parent.pulseEventId, context);
        }
        this.storage.setItem(`vibes-dismiss`, Date.now().toString());
        this.metricsRepo && this.metricsRepo.dismissedEvent(workflow);
    }

    /**
     * Get whether the user has prompted to be asked later and the related meta data
     */
    public getAskMeLater(clear = false): AskMeLater | null {
        const key = 'vibes-aml';
        const item = this.storage.getItem(key);

        // clear the cache if prompted
        if (clear) {
            this.storage.removeItem(key);
        }

        if (!item) {
            return null;
        }

        let parsed;
        try {
            parsed = JSON.parse(item);
        } catch (e) {
            this.storage.removeItem(key);
            return null;
        }

        // if (typeof parsed === 'object' && parsed.hasOwnProperty('time') && parsed.hasOwnProperty('workflow')) {
        if (typeof  parsed === 'object' && Object.prototype.hasOwnProperty.call(parsed, "time") && Object.prototype.hasOwnProperty.call(parsed, "workflow")) {
            return parsed;
        }

        this.storage.removeItem(key);

        return null;
    }

    /**
     * indicates that a customer was targeted for a Vibes questionaire.
     * @param workflowName the workflow that they have been targeted for
     */
    public customerWasTargeted(workflowName: string, type = '', askMeLater = false) {
        this.metricsRepo && this.metricsRepo.customerTargetedEvent(workflowName, type, askMeLater);
    }

    /**
     * Get whether the user has dismissed some specified number of seconds
     */
    public hasDismissedWithin(expirationDuration: number): boolean {
        return this.checkKey('vibes-dismiss', expirationDuration);
    }

    public hasSubmittedWithin(expirationDuration: DurationSeconds): boolean {
        return this.checkKey('vibes-submit', expirationDuration);
    }

    public saveSubmission(workflow: string, type = '') {
        this.storage.setItem('vibes-submit', Date.now().toString());
        this.metricsRepo && this.metricsRepo.submittedEvent(workflow, type);
    }

    public saveFormInteraction(workflow: string, type  = '') {
        this.metricsRepo && this.metricsRepo.formInteractionEvent(workflow, type);
    }

    private checkKey(key: string, expirationDuration: DurationSeconds) {
        const item = this.storage.getItem(key);

        if (!item) {
            return false;
        }

        const time = parseInt(item);


        // if the value isn't parsed as a time, it is invalid. remove it and return false
        if (!time) {
            this.storage.removeItem(key);
            return false;
        }

        // if number of seconds since the user dismissed is less than our duration, return false
        const timePassed = (Date.now() - time);

        if (timePassed < (expirationDuration * 1000)) {
            return true;
        }

        this.storage.removeItem(key);

        return false;
    }
}

interface AskMeLater {
    time: number;
    workflow: string;
}
